import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../contexts/SessionContext';
import { MainLayout } from './MainLayout';
import { supabase } from '../lib/supabase_client';

interface Props {
  children: JSX.Element;
}

const ProtectedRoute: React.FC<Props> = ({ children }) => {
  const { user, loading, profileMissing } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const lastCheckedPathRef = useRef<string | null>(null);
  const isCheckingAccessRef = useRef(false);

  // ✅ Check on every route change, not just once
  useEffect(() => {
    // Only check if user is logged in, not loading, and path changed
    if (loading || !user || isCheckingAccessRef.current) return;
    if (location.pathname === '/complete-profile' || location.pathname === '/setup-profile') return;

    // Skip if we already checked this path
    if (lastCheckedPathRef.current === location.pathname) return;

    const checkUserAccess = async () => {
      isCheckingAccessRef.current = true;
      try {
        // ✅ Verify actual org access in database (not just cached orgs)
        const { data: memberships, error: membershipError } = await supabase
          .from('organization_members')
          .select('org_id')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .limit(1);

        if (membershipError) {
          console.error('Error checking memberships:', membershipError);
          // Don't refresh on error - SessionContext already has latest data
          lastCheckedPathRef.current = location.pathname;
          return;
        }

        // ✅ If user has no active memberships, check invitations
        if (!memberships || memberships.length === 0) {
          // No need to refresh - SessionContext already has latest orgs list
          
          // Check user_invitations
          const { data: invitations, error: invitationError } = await supabase
            .from('user_invitations')
            .select('id, status, org_id, role_id')
            .eq('email', user.email || '')
            .order('invited_at', { ascending: false })
            .limit(1);

          if (invitationError) {
            console.error('Error checking invitations:', invitationError);
            lastCheckedPathRef.current = location.pathname;
            return;
          }

          if (invitations && invitations.length > 0) {
            const invitation = invitations[0];
            
            if (invitation.status === 'pending') {
              // User has pending invitation - redirect to complete profile
              console.log('User has pending invitation, redirecting to complete-profile');
              navigate(`/complete-profile?invitation=${invitation.id}`, { replace: true });
              return;
            } else if (invitation.status === 'accepted') {
              // User was removed from org after accepting - redirect to no-organization
              console.log('User has accepted invitation but no org access, redirecting to no-organization');
              navigate('/no-organization', { replace: true });
              return;
            }
          } else {
            // No invitations found - redirect to no-organization
            console.log('User has no orgs and no invitations, redirecting to no-organization');
            navigate('/no-organization', { replace: true });
            return;
          }
        } else {
          // ✅ User has active memberships - no need to refresh (already loaded by SessionContext)
          // The membership check above already verified user has access
        }
        
        // Mark this path as checked
        lastCheckedPathRef.current = location.pathname;
      } catch (error) {
        console.error('Error checking user access:', error);
      } finally {
        isCheckingAccessRef.current = false;
      }
    };

    checkUserAccess();
  }, [user, loading, location.pathname, navigate]);

  // Reset checked path when user changes
  useEffect(() => {
    lastCheckedPathRef.current = null;
    // Force a fresh access check for the new user
    isCheckingAccessRef.current = false;
  }, [user?.id]);

  // Show loading while checking access
  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-4">
            <svg className="animate-spin h-12 w-12 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600 text-sm">Loading...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!user) {
    const from = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/signin" replace state={{ from }} />;
  }

  // Gate: authenticated user has no user_profiles row — must complete setup first
  if (profileMissing) {
    return <Navigate to="/setup-profile" replace />;
  }

  return children;
};

export default ProtectedRoute;